import { ConnectedAccountType } from '@/types';

export const COMMAND_PALETTE_ALL_TAB_KEY = 'all';

export const commandPaletteSearchableProviderGroups = [
    'airtable',
    'asana',
    'attio',
    'box',
    'clickup',
    'close',
    // 'coda',
    'confluence',
    // 'docusign',
    'dropbox',
    'googleDrive',
    'googleSheets',
    'hubspot',
    // 'miro',
    'monday',
    'notion',
    'pipedrive',
    'salesforce',
    'slack',
    'smartsheet',
    // 'wealthbox',
    'wrike',
] as const;

export type CommandPaletteSearchableProviderGroup = (typeof commandPaletteSearchableProviderGroups)[number];

const commandPaletteSearchableProviderGroupSet = new Set<string>(commandPaletteSearchableProviderGroups);

export const isCommandPaletteSearchableProviderGroup = (
    group: string | null | undefined,
): group is CommandPaletteSearchableProviderGroup => !!group && commandPaletteSearchableProviderGroupSet.has(group);

export type CommandPaletteTabSectionKey = 'platform' | 'dataSources';

export type CommandPaletteTabPreferenceSection = {
    hidden?: string[];
    order?: string[];
    visible?: string[];
};

export type CommandPalettePreferences = {
    tabs?: Partial<Record<CommandPaletteTabSectionKey, CommandPaletteTabPreferenceSection>>;
};

export type CommandPaletteTabModel = {
    connectedAccountId?: string;
    description: string;
    groups?: string[];
    key: string;
    label: string;
    locked?: boolean;
    defaultHidden?: boolean;
    providerGroup?: string;
    section: CommandPaletteTabSectionKey;
    sourceType?: ConnectedAccountType;
};

export type CommandPaletteTabSection = {
    items: CommandPaletteTabModel[];
    key: CommandPaletteTabSectionKey;
    lockedItemKeys?: string[];
    title: string;
};

export const commandPalettePlatformTabs: CommandPaletteTabModel[] = [
    {
        key: COMMAND_PALETTE_ALL_TAB_KEY,
        label: 'All Results',
        description: 'Search across all commands, workspace resources, companies, and connected data sources.',
        section: 'platform',
        locked: true,
    },
    {
        key: 'actions',
        label: 'Actions',
        description: 'Run navigation, creation, account, workspace, help, and miscellaneous actions.',
        groups: ['resourceActions', 'navigation', 'quickActions', 'account', 'workspace', 'help', 'miscellaneous'],
        section: 'platform',
    },
    {
        key: 'workspace',
        label: 'Workspace',
        description: 'Search folders, investments, memos, pipelines, dashboards, lists, and teams.',
        groups: ['folders', 'investments', 'memos', 'pipelines', 'dashboards', 'lists', 'teams'],
        section: 'platform',
    },
    {
        key: 'companies',
        label: 'Companies',
        description: 'Search global company results and market intelligence.',
        groups: ['companies'],
        section: 'platform',
    },
    {
        key: 'people',
        label: 'People',
        description: 'Search email recipients, contacts, and calendar participants.',
        groups: ['people'],
        section: 'platform',
        defaultHidden: true,
    },
    {
        key: 'documents',
        label: 'Documents',
        description: 'Search memos, files, and document-oriented connected data sources.',
        groups: [
            'memos',
            'googleDrive',
            'googleSheets',
            'notion',
            'salesforce',
            'box',
            'dropbox',
            // 'coda',
            'confluence',
            // 'docusign',
            'smartsheet',
        ],
        section: 'platform',
        defaultHidden: true,
    },
];

const connectedAccountTypeToProviderGroupMap: Partial<Record<ConnectedAccountType, string>> = {
    [ConnectedAccountType.AIRTABLE]: 'airtable',
    [ConnectedAccountType.ASANA]: 'asana',
    [ConnectedAccountType.ATTIO]: 'attio',
    [ConnectedAccountType.BOX]: 'box',
    [ConnectedAccountType.CLICKUP]: 'clickup',
    [ConnectedAccountType.CLOSE]: 'close',
    // [ConnectedAccountType.CODA]: 'coda',
    [ConnectedAccountType.CONFLUENCE]: 'confluence',
    // [ConnectedAccountType.DOCUSIGN]: 'docusign',
    [ConnectedAccountType.DROPBOX]: 'dropbox',
    [ConnectedAccountType.GMAIL]: 'gmail',
    [ConnectedAccountType.GOOGLE_DRIVE]: 'googleDrive',
    [ConnectedAccountType.GOOGLE_SHEETS]: 'googleSheets',
    [ConnectedAccountType.HUBSPOT]: 'hubspot',
    [ConnectedAccountType.MICROSOFT_365_OUTLOOK]: 'microsoft365Outlook',
    [ConnectedAccountType.MICROSOFT_ONEDRIVE]: 'oneDrive',
    [ConnectedAccountType.MICROSOFT_SHAREPOINT]: 'microsoftSharePoint',
    // [ConnectedAccountType.MIRO]: 'miro',
    [ConnectedAccountType.MONDAY]: 'monday',
    [ConnectedAccountType.NOTION]: 'notion',
    [ConnectedAccountType.PIPEDRIVE]: 'pipedrive',
    [ConnectedAccountType.SALESFORCE]: 'salesforce',
    [ConnectedAccountType.SLACK]: 'slack',
    [ConnectedAccountType.SMARTSHEET]: 'smartsheet',
    // [ConnectedAccountType.WEALTHBOX]: 'wealthbox',
    [ConnectedAccountType.WRIKE]: 'wrike',
};

export const providerGroupForConnectedAccountType = (type?: ConnectedAccountType | string) => {
    if (!type) {
        return 'dataSources';
    }

    const knownProviderGroup = connectedAccountTypeToProviderGroupMap[type as ConnectedAccountType];

    if (knownProviderGroup) {
        return knownProviderGroup;
    }

    const [firstPart, ...rest] = String(type)
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .split(/\s+/);

    return [firstPart?.toLowerCase(), ...rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1))].join('');
};

export const getCommandPaletteSectionPreferences = (
    preferences: CommandPalettePreferences | null | undefined,
    sectionKey: CommandPaletteTabSectionKey,
): CommandPaletteTabPreferenceSection => preferences?.tabs?.[sectionKey] ?? {};

export const orderCommandPaletteTabItems = (
    items: CommandPaletteTabModel[],
    preferences?: CommandPaletteTabPreferenceSection,
) => {
    const existingKeys = new Set(items.map((item) => item.key));
    const orderedKeys = Array.from(new Set(preferences?.order?.filter((key) => existingKeys.has(key)) ?? []));
    const orderedItems = orderedKeys
        .map((key) => items.find((item) => item.key === key))
        .filter(Boolean) as CommandPaletteTabModel[];
    const remainingItems = items.filter((item) => !orderedKeys.includes(item.key));

    return [...orderedItems, ...remainingItems];
};

export const applyCommandPaletteTabPreferences = (
    items: CommandPaletteTabModel[],
    preferences?: CommandPaletteTabPreferenceSection,
) => {
    const hidden = new Set(preferences?.hidden ?? []);
    const visible = new Set(preferences?.visible ?? []);

    return orderCommandPaletteTabItems(items, preferences).filter((item) => {
        if (item.locked) {
            return true;
        }

        if (hidden.has(item.key)) {
            return false;
        }

        if (item.defaultHidden && !visible.has(item.key)) {
            return false;
        }

        return true;
    });
};
